import { useState, useEffect, useContext } from 'react';
import { Context } from '../AuthContext';

import { useCategory } from '../CategoryContext';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

import Card from './Card/Card';
import { categories } from '../utils/cardInfo';
import { capitalizeFirstLetter } from '../utils/functions';
import Disclaimer from './Disclaimer';

export default function ReviewNewResourcesModal({
  open,
  onClose,
  onEdit,
  vertical_id,
  items,
  setItems,
  lastRun,
}) {
  const { updateCount } = useCategory();
  const { user } = useContext(Context);

  const [checkedItems, setCheckedItems] = useState({});
  const [busy, setBusy] = useState(false);

  const handleToggle = (item) => {
    setCheckedItems((prev) => ({
      ...prev,
      [item._id]: !prev[item._id],
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setBusy(true);

    const selectedByCategory = {};

    Object.entries(categories).forEach(([rawCategory]) => {
      const categoryItems = items?.[rawCategory] || [];

      const selectedIds = categoryItems
        .filter((item) => checkedItems[item._id])
        .map((item) => item._id);

      const derivedCategory =
        rawCategory === 'authors' || rawCategory === 'inventors' ? 'people' : rawCategory;

      if (selectedIds.length > 0) {
        selectedByCategory[rawCategory] = selectedIds;
        updateCount(derivedCategory, selectedIds.length);
      }
    });

    await addItems(selectedByCategory);
    setItems({});
    onEdit({ arg: 'reviewed', value: true });
    setBusy(false);
    onClose();
  };

  const addItems = async (items) => {
    if (!user) return;
    const token = await user.getIdToken();
    const res = await fetch(
      `${process.env.REACT_APP_FLASK_WEBSERVER}edit_item_status?vertical_id=${vertical_id}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(items),
      }
    );
  };

  const doNotAddAnything = async () => {
    if (!user) return;
    const token = await user.getIdToken();
    const res = await fetch(
      `${process.env.REACT_APP_FLASK_WEBSERVER}reject_all_items?vertical_id=${vertical_id}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );
  };

  useEffect(() => {
    if (!open) return;
    setCheckedItems({});
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth={true} maxWidth="md">
      <DialogTitle align="left" sx={{ fontWeight: 700 }}>
        Review New Resources
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'left' }}>
        <DialogContentText sx={{ color: 'black' }}>{lastRun}</DialogContentText>
        <DialogContentText sx={{ fontSize: '14px', marginBottom: '12px' }}>
          Select any resources you’d like to import into your Vertical
          <Disclaimer />
        </DialogContentText>

        <form onSubmit={handleSubmit} id="review-form">
          {items &&
            Object.entries(categories).map(([category, props]) => {
              const categoryItems = items[category];

              if (!Array.isArray(categoryItems) || categoryItems.length === 0) {
                return null;
              }

              let category_;
              if (category === 'grants') {
                category_ = 'Funding';
              } else if (category === 'papers') {
                category_ = 'Publications';
              } else {
                category_ = category;
              }

              return (
                <>
                  <DialogContentText
                    sx={{ fontWeight: 700, color: 'black', fontSize: '18px', marginLeft: '45px' }}
                  >
                    {capitalizeFirstLetter(category_)}
                  </DialogContentText>
                  <DialogContentText sx={{ fontSize: '12px', marginLeft: '45px' }}>
                    {categoryItems.length} result{categoryItems.length > 1 && 's'}
                  </DialogContentText>

                  {categoryItems.map((item) => (
                    <div
                      key={item._id}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!checkedItems[item._id]}
                            onChange={() => handleToggle(item)}
                          />
                        }
                      />
                      <div style={{ flex: 1 }}>
                        <Card
                          category={category}
                          details={item}
                          editor={false}
                          cardHeader={props?.cardHeader}
                          icons={props?.icons}
                          urlIcons={props?.urlIcons}
                          dotColor={props?.dotColor}
                          vertical_id={vertical_id}
                          visibleBookmark={false}
                          openNewTab={true}
                        />
                      </div>
                    </div>
                  ))}
                </>
              );
            })}
        </form>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Button
          variant="contained"
          color="error"
          disabled={busy || Object.values(checkedItems).filter(Boolean).length > 0}
          onClick={async () => {
            setBusy(true);
            await doNotAddAnything();
            setItems({});
            onEdit({ arg: 'reviewed', value: true });
            setBusy(false);
            onClose();
          }}
        >
          Do not add anything
        </Button>

        <div
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}
        >
          <div style={{ display: 'flex', gap: '4px' }}>
            <Button variant="contained" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              form="review-form"
              disabled={busy || Object.values(checkedItems).filter(Boolean).length === 0}
            >
              Confirm & Add to Vertical
            </Button>
          </div>
          <DialogContentText
            sx={{
              maxWidth: '430px',
              fontSize: '11px',
              lineHeight: 1.35,
              color: 'text.secondary',
              textAlign: 'left',
              margin: 0,
            }}
          >
            Currently, only the selected resources will be retained; unselected resources will be
            discarded. TSP will offer in the future the ability to revisit discarded items, but not
            yet.
          </DialogContentText>
        </div>
      </DialogActions>
    </Dialog>
  );
}
